import { useSearchParams } from 'react-router-dom'
import { useLoad } from '@/features/administration/hooks'
import { listElections, type TurnoutElection } from './api'

/**
 * Which election is on screen.
 *
 * ## The thing this replaces
 *
 * Seven pages had a mock fixture's uuid — `00000001-6666-4000-9000-000000000001` —
 * written into their source, two of them as a `useState` default that nothing could
 * change. Against the real server that id does not exist, so every one of those screens
 * showed an empty state on a deployment with live elections, and nothing said why.
 *
 * ## How it resolves
 *
 * The election lives in the URL as `?election=<id>`, so a link to a station grid is a
 * link to *that* election's station grid — the same rule the polling module follows for
 * its poll picker. With no parameter it falls back to the first election the server
 * returns that is not `CLOSED`, which is the one somebody opening the module almost
 * always wants; if every election is closed it falls back to the most recent, because an
 * empty screen is a worse answer than a historical one.
 *
 * Returns `null` for `electionId` while the list is still loading and when the
 * deployment genuinely has no elections. Callers must render that case rather than
 * fetching against `null` — {@link ElectionGate} exists so they do not have to think
 * about it.
 */
export interface ElectionSelection {
  electionId: string | null
  election: TurnoutElection | null
  elections: TurnoutElection[]
  loading: boolean
  error: string | null
  select: (id: string) => void
  reload: () => void
}

export function useElection(): ElectionSelection {
  const [params, setParams] = useSearchParams()
  const requested = params.get('election')
  const loaded = useLoad(listElections, [])

  const elections = loaded.data ?? []
  const live = elections.filter((e) => e.statusCode !== 'CLOSED')
  const fallback = live[0] ?? elections[0] ?? null

  // A requested id that the server does not return is treated as absent rather than
  // honoured: a stale bookmark should land on a usable screen, not an empty one.
  const election = (requested && elections.find((e) => e.id === requested)) || fallback

  const select = (id: string) => {
    const next = new URLSearchParams(params)
    if (id) next.set('election', id)
    else next.delete('election')
    setParams(next, { replace: true })
  }

  return {
    electionId: election?.id ?? null,
    election: election ?? null,
    elections,
    loading: loaded.loading,
    error: loaded.error,
    select,
    reload: loaded.reload,
  }
}
